import { CONDITIONS } from "@/components/inventory/ConditionPicker";
import type { PublicBrowseSetFacet } from "@/hooks/usePublicBrowse";
import { type BrowseRefinements, kindRefinements } from "@/lib/browse/browseFilter";
import { GAMES } from "@/lib/catalog/games";
import { type Facet, facet } from "@/lib/filters/facet";
import { kindFacets, slabFacets } from "@/lib/filters/kindFacets";
import { priceRangeLabel } from "@/lib/filters/priceRange";
import { KINDS } from "@/lib/inventory/kind";

/** A storefront filter change; the screen applies each present key to its own state. */
export type StorefrontPatch = {
  game?: string | null;
  set?: PublicBrowseSetFacet | null;
  refinements?: Partial<BrowseRefinements>;
  /** A Location's id, on a page that files Location under Filters (the shop). */
  location?: string | null;
};

export type StorefrontFacet = Facet<StorefrontPatch>;

export const gameLabel = (value: string): string =>
  GAMES.find((game) => game.value === value)?.label
    ?? value.charAt(0).toUpperCase() + value.slice(1);

/** A set is identified by its (game, name) pair, never by name alone — two games can each carry a
 *  "Promos". The pair travels as one option value. */
const setValue = (s: PublicBrowseSetFacet): string => JSON.stringify([s.game, s.name]);

/** Keys the sheet shows inline (Price inputs, the Type switch) rather than as summary rows. */
export const INLINE_FACETS = new Set(["kind", "price"]);

/**
 * The storefront's filters on the shared facet model, in the order the sheet lists them.
 *
 * Type and price live inline in the sheet but are facets too, so each gets a removable chip.
 * The Type decides the rest, by the rule inventory follows (`kindFacets`): Condition (multi-select,
 * NM or LP) for raw copies or no Type, Grader and Grade for Graded, neither for Sealed. A one-game table offers no Game facet, and its implicit
 * game is not counted as a filter. `showPrice` is false at a table that shows no prices, since a
 * range would match nothing. Location is a facet only where the page passes `locations` (the
 * shop); at a table it stays one tap away as pills, because a customer there is told "it's in the
 * showcase".
 */
export function storefrontFacets(
  { games, sets, game, set, refinements, showPrice, locations = [], location = null }: {
    games: string[];
    sets: PublicBrowseSetFacet[];
    game: string | null;
    set: PublicBrowseSetFacet | null;
    refinements: BrowseRefinements;
    showPrice: boolean;
    /** The shop's Locations, offered as a sheet facet; the table keeps them as pills instead. */
    locations?: { value: string; label: string; }[];
    location?: string | null;
  },
): StorefrontFacet[] {
  const priced = showPrice && !!(refinements.minPrice.trim() || refinements.maxPrice.trim());
  // Condition for raw copies, Grader and Grade for slabs, neither for sealed (`kindFacets`).
  const applies = kindFacets(refinements.kind);
  const shownSets = game ? sets.filter((s) => s.game === game) : sets;
  // A Set from a deep link names itself before the Set list has loaded.
  const visibleSets = set && !shownSets.some((s) => setValue(s) === setValue(set))
    ? [...shownSets, set]
    : shownSets;
  const all: (StorefrontFacet | false)[] = [
    facet<StorefrontPatch>({
      key: "kind",
      label: "Type",
      options: KINDS.map((k) => ({ value: k.kind, label: k.label })),
      value: refinements.kind,
      restingValue: null,
      restingLabel: "All",
      // Clears what the new Type cannot carry, as inventory's Type does (`kindFacets`).
      pick: (kind) => ({
        refinements: kindRefinements(KINDS.find((k) => k.kind === kind)?.kind ?? null),
      }),
    }),
    priced && facet<StorefrontPatch>({
      key: "price",
      label: "Price",
      options: [{
        value: "range",
        label: priceRangeLabel({ min: refinements.minPrice, max: refinements.maxPrice }),
      }],
      value: "range",
      restingValue: null,
      restingLabel: "Any price",
      pick: () => ({ refinements: { minPrice: "", maxPrice: "" } }),
    }),
    applies.condition && facet<StorefrontPatch>({
      key: "condition",
      label: "Condition",
      options: CONDITIONS.map((c) => ({ value: c, label: c, a11yLabel: `Condition ${c}` })),
      value: null,
      restingValue: null,
      restingLabel: "Any condition",
      pick: () => ({ refinements: { conditions: [] } }),
      multi: {
        values: refinements.conditions,
        toggle: (c) => ({
          refinements: {
            conditions: refinements.conditions.includes(c)
              ? refinements.conditions.filter((x) => x !== c)
              : [...refinements.conditions, c],
          },
        }),
      },
    }),
    // A slab's Grader and Grade floor, only once the Type is Graded: the same pair inventory offers.
    ...(applies.slab
      ? slabFacets<StorefrontPatch>({
        grader: refinements.grader,
        gradeMin: refinements.gradeMin,
        pickGrader: (grader) => ({ refinements: { grader } }),
        pickGrade: (gradeMin) => ({ refinements: { gradeMin } }),
      })
      : []),
    games.length > 1 && facet<StorefrontPatch>({
      key: "game",
      label: "Game",
      options: games.map((g) => ({
        value: g,
        label: gameLabel(g),
        a11yLabel: `Filter by ${gameLabel(g)}`,
      })),
      value: game,
      restingValue: null,
      restingLabel: "All games",
      // A Set belongs to one Game, so changing the Game drops a Set it cannot hold.
      pick: (next) => ({ game: next, ...(set && next && set.game !== next ? { set: null } : {}) }),
    }),
    visibleSets.length > 0 && facet<StorefrontPatch>({
      key: "set",
      label: "Set",
      // With no Game chosen the same name can come from two games, so the label says which.
      options: visibleSets.map((s) => ({
        value: setValue(s),
        label: game || games.length <= 1 ? s.name : `${s.name} (${gameLabel(s.game)})`,
        a11yLabel: `Filter by set ${s.name}`,
      })),
      value: set ? setValue(set) : null,
      restingValue: null,
      restingLabel: "All sets",
      // Picking a Set also picks its Game, so the two never disagree.
      pick: (value) => {
        const picked = visibleSets.find((s) => setValue(s) === value) ?? null;
        return picked ? { game: picked.game, set: picked } : { set: null };
      },
      searchable: true,
    }),
    locations.length > 1 && facet<StorefrontPatch>({
      key: "location",
      label: "Location",
      options: locations,
      value: location,
      restingValue: null,
      restingLabel: "All locations",
      pick: (next) => ({ location: next }),
    }),
  ];
  return all.filter((f): f is StorefrontFacet => !!f);
}
